import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocDivider } from 'commons/components/components-wrapper/poc-divider.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { useTranslation } from 'core/i18n/i18n';
import React from 'react';
import {
    ACTIVE_DIRECTORY_INTERVALS,
    MAX_AD_FIELD_LENGTH,
} from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/settings-integrations-active-directory.constants';
import {
    ActiveDirectoryDetailsFormState,
    TestConnectionState,
} from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/types/active-directory.type';
import { ActiveDirectoryConnectionStatus } from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/components/active-directory-connection-status';
import { ActiveDirectoryDetailsItem } from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/components/settings-integrations-active-directory-details/active-directory-details-item.vm';
import { ConnectionFormFieldErrors } from './active-directory-connection-details-form.business';
import { useConnectionDetailsForm } from './use-connection-details-form.hook';
import {
    ActiveDirectoryConnectionDetailsFormFieldsWrapper,
    ActiveDirectoryConnectionDetailsFormHeaderContent,
    ActiveDirectoryConnectionDetailsFormHeaderTitleWrapper,
    InputStyled,
    LastSyncPocTypography,
    PocComponentHeaderStyled,
    OwcComponentHeaderSuffixWrapperStyled,
    SelectStyled,
    SettingsCardLayoutStyled,
} from './active-directory-connection-details-form.style';

interface ActiveDirectoryConnectionDetailsFormProps {
    isDirty: boolean;
    isEditMode: boolean;
    activeDirectory?: ActiveDirectoryDetailsItem;
    formState: ActiveDirectoryDetailsFormState;
    testConnectionState: TestConnectionState;
    fieldErrors?: ConnectionFormFieldErrors;
    onChangeField: (field: string, value: string) => void;
    onTestConnection: () => Promise<void>;
    onEditConnectionDetails: VoidFunction;
}

export const ActiveDirectoryConnectionDetailsForm: React.FC<ActiveDirectoryConnectionDetailsFormProps> = ({
    isDirty,
    isEditMode,
    activeDirectory,
    formState,
    testConnectionState,
    fieldErrors = {},
    onChangeField,
    onTestConnection,
    onEditConnectionDetails,
}) => {
    const { t } = useTranslation();

    const {
        editable,
        canTestConnection,
        currentConnectionStatus,
        currentConnectionErrorMessage,
        lastSyncDate,
        lastSyncHour,
        computeFieldValidity,
        handleBlur,
        handleChangeField,
        handleActivateEditMode,
        handleTestConnection,
    } = useConnectionDetailsForm({
        isDirty,
        isEditMode,
        activeDirectory,
        formState,
        testConnectionState,
        fieldErrors,
        onChangeField,
        onTestConnection,
        onEditConnectionDetails,
    });

    return (
        <SettingsCardLayoutStyled rounded>
            <PocComponentHeaderStyled slot="header">
                <ActiveDirectoryConnectionDetailsFormHeaderContent>
                    <ActiveDirectoryConnectionDetailsFormHeaderTitleWrapper>
                        <PocTypography element="h6" variant="title6">
                            {t('settings.integrations.active-directory.connection.form.title')}
                        </PocTypography>
                        <ActiveDirectoryConnectionStatus
                            status={currentConnectionStatus}
                            errorMessage={currentConnectionErrorMessage}
                        />
                    </ActiveDirectoryConnectionDetailsFormHeaderTitleWrapper>
                    <LastSyncPocTypography variant="body1">
                        {lastSyncDate && lastSyncHour
                            ? t('settings.integrations.active-directory.host.info.connection.lastSync', {
                                  date: lastSyncDate,
                                  hour: lastSyncHour,
                              })
                            : t('settings.integrations.active-directory.host.info.connection.notSync')}
                    </LastSyncPocTypography>
                </ActiveDirectoryConnectionDetailsFormHeaderContent>
                <OwcComponentHeaderSuffixWrapperStyled slot="suffix">
                    {editable ? (
                        <PocButton variant="secondary" disabled={!canTestConnection} onClick={handleTestConnection}>
                            {t('settings.integrations.active-directory.connection.form.button.testConnection')}
                        </PocButton>
                    ) : (
                        <PocButton variant="secondary" onClick={handleActivateEditMode}>
                            {t('settings.integrations.active-directory.connection.form.button.edit')}
                        </PocButton>
                    )}
                </OwcComponentHeaderSuffixWrapperStyled>
            </PocComponentHeaderStyled>
            <ActiveDirectoryConnectionDetailsFormFieldsWrapper>
                <InputStyled
                    name="name"
                    label="settings.integrations.active-directory.connection.form.directoryName"
                    placeholder="settings.integrations.active-directory.connection.form.directoryName.placeholder"
                    value={formState.name}
                    required
                    maxlength={MAX_AD_FIELD_LENGTH}
                    validity={computeFieldValidity('name')}
                    onChange={handleChangeField}
                    onCustomBlur={() => handleBlur('name')}
                    inline
                    spaceForError
                    autofocus
                    noClearIcon={false}
                    disabled={!editable}
                />
                <PocDivider />
                <InputStyled
                    name="url"
                    label="settings.integrations.active-directory.connection.form.url"
                    placeholder="settings.integrations.active-directory.connection.form.url.placeholder"
                    value={formState.url}
                    required
                    maxlength={MAX_AD_FIELD_LENGTH}
                    validity={computeFieldValidity('url')}
                    onChange={handleChangeField}
                    onCustomBlur={() => handleBlur('url')}
                    inline
                    spaceForError
                    noClearIcon={false}
                    disabled={!editable}
                />
                <PocDivider />
                <InputStyled
                    name="group"
                    label="settings.integrations.active-directory.connection.form.group"
                    placeholder="settings.integrations.active-directory.connection.form.group.placeholder"
                    value={formState.group}
                    required
                    maxlength={MAX_AD_FIELD_LENGTH}
                    validity={computeFieldValidity('group')}
                    onChange={handleChangeField}
                    onCustomBlur={() => handleBlur('group')}
                    inline
                    spaceForError
                    noClearIcon={false}
                    disabled={!editable}
                />
                <PocDivider />
                <SelectStyled
                    name="interval"
                    label="settings.integrations.active-directory.connection.form.interval"
                    placeholder="settings.integrations.active-directory.connection.form.interval.placeholder"
                    value={formState.interval}
                    options={Object.keys(ACTIVE_DIRECTORY_INTERVALS).map((key) => ({
                        text: t(`settings.integrations.active-directory.connection.form.interval.options.${key}`),
                        value: ACTIVE_DIRECTORY_INTERVALS[key],
                    }))}
                    required
                    validity={computeFieldValidity('interval')}
                    onChange={(field, value) => handleChangeField(field, value?.[0] as string)}
                    onClose={() => handleBlur('interval')}
                    inline
                    spaceForError
                    disabled={!editable}
                />
                <PocDivider />
                <InputStyled
                    name="username"
                    label="settings.integrations.active-directory.connection.form.username"
                    placeholder="settings.integrations.active-directory.connection.form.username.placeholder"
                    value={formState.username}
                    required
                    maxlength={MAX_AD_FIELD_LENGTH}
                    validity={computeFieldValidity('username')}
                    onChange={handleChangeField}
                    onCustomBlur={() => handleBlur('username')}
                    inline
                    spaceForError
                    noClearIcon={false}
                    disabled={!editable}
                />
                <PocDivider />
                <InputStyled
                    name="userGroup"
                    label="settings.integrations.active-directory.connection.form.userGroup"
                    placeholder="settings.integrations.active-directory.connection.form.userGroup.placeholder"
                    value={formState.userGroup}
                    required
                    maxlength={MAX_AD_FIELD_LENGTH}
                    validity={computeFieldValidity('userGroup')}
                    onChange={handleChangeField}
                    onCustomBlur={() => handleBlur('userGroup')}
                    inline
                    spaceForError
                    noClearIcon={false}
                    disabled={!editable}
                />
                <PocDivider />
                <InputStyled
                    name="password"
                    label="settings.integrations.active-directory.connection.form.password"
                    placeholder="settings.integrations.active-directory.connection.form.password.placeholder"
                    value={formState.password}
                    required
                    maxlength={MAX_AD_FIELD_LENGTH}
                    validity={computeFieldValidity('password')}
                    onChange={handleChangeField}
                    onCustomBlur={() => handleBlur('password')}
                    inline
                    spaceForError
                    type="password"
                    noClearIcon={false}
                    disabled={!editable}
                />
            </ActiveDirectoryConnectionDetailsFormFieldsWrapper>
        </SettingsCardLayoutStyled>
    );
};
